/**
 * Atoll authentication functions — shared across all Atoll-compliant frontends.
 *
 * authenticateUser: POST /auth/token with {username, password, client_id}
 * refreshSession: POST /auth/refresh with {refresh_token, client_id} + Bearer header
 * fnUserLoggedOut: clear tokens and session state on logout
 *
 * Both use the OAuth2-style response shape:
 * { access_token, token_type, expires_in, refresh_token, user }
 *
 * The client_id comes from initAtoll({ clientId: "apestaart" }).
 *
 * Moved from services/auth/user.ts — rewritten from Sputnik browser auth
 * (Basic auth + /renew + envelope response) to Atoll service token path.
 */

import { getAtollServiceURL, getAtollClientId } from "./init";
import {
  getJwtToken,
  setJwtToken,
  getRefreshToken,
  setRefreshToken,
  clearRefreshToken,
  getTokenExpiresAt,
  setTokenExpiresAt,
  clearTokenExpiresAt,
} from "../services/shared";
import { addLog, Severity } from "./redux/consoleSlice";
import {
  refreshStarted,
  refreshSucceeded,
  refreshFailed,
  refreshQueueIncrement,
  refreshQueueDecrement,
  setLoginRequired,
} from "./redux/authDebugSlice";

function logError(dispatch: any, ...args: any[]) {
  const message = args.map(a =>
    typeof a === 'string' ? a : a instanceof Error ? a.message : JSON.stringify(a)
  ).join(' ');
  if (dispatch) {
    try {
      dispatch(addLog({ severity: Severity.Error, message }));
    } catch {
      console.error(message);
    }
  } else {
    console.error(message);
  }
}

/**
 * Authenticate a user via Atoll's /auth/token endpoint.
 * Posts JSON {username, password, client_id} and returns the OAuth2 response.
 *
 * @param username - User's login
 * @param password - User's password
 * @param outcomeCallback - Called with (success: boolean, data: any)
 * @param dispatch - Redux dispatch for error surfacing
 */
export async function authenticateUser(
  username: string,
  password: string,
  outcomeCallback: (success: boolean, data: any) => void,
  dispatch: any
): Promise<void> {
  try {
    const baseURL = getAtollServiceURL();
    const clientId = getAtollClientId();

    const response = await fetch(`${baseURL}auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password, client_id: clientId }),
    });

    const data = await response.json();

    if (response.ok && data.access_token) {
      // Store tokens — refresh token in sessionStorage (per-tab) to support multi-tab sessions
      setJwtToken(data.access_token);
      if (data.refresh_token) setRefreshToken(data.refresh_token);
      if (typeof data.expires_in === 'number') {
        setTokenExpiresAt(Date.now() + data.expires_in * 1000);
      }
      outcomeCallback(true, data);
    } else {
      outcomeCallback(false, { error: data.error || 'Authentication failed' });
    }
  } catch (error) {
    logError(dispatch, error);
    if (error instanceof Error) {
      outcomeCallback(false, { error: `${error.name}: ${error.message}` });
    } else {
      outcomeCallback(false, { error: 'Unknown error' });
    }
  }
}

// Refresh near-expiry window: if the token has this much life left or less,
// a fetch will kick off a background refresh before sending the request.
const REFRESH_THRESHOLD_MS = 60_000;

// Single-flight lock. Shared between the 401-retry path and the preemptive
// ensureFreshToken path so concurrent fetches can never trigger parallel
// refreshes (which would invalidate each other's single-use refresh tokens).
let refreshingPromise: Promise<{ success: boolean; data: any }> | null = null;

async function _performRefresh(
  dispatch: any
): Promise<{ success: boolean; data: any }> {
  if (dispatch) {
    try { dispatch(refreshStarted()); } catch {}
  }
  try {
    const baseURL = getAtollServiceURL();
    const clientId = getAtollClientId();
    const accessToken = getJwtToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const refreshToken = getRefreshToken();
    const response = await fetch(`${baseURL}auth/refresh`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ client_id: clientId, refresh_token: refreshToken }),
    });

    const data = await response.json();

    if (response.ok && data.access_token) {
      setJwtToken(data.access_token);
      if (data.refresh_token) setRefreshToken(data.refresh_token);
      if (typeof data.expires_in === 'number') {
        setTokenExpiresAt(Date.now() + data.expires_in * 1000);
      }
      if (dispatch) {
        try { dispatch(refreshSucceeded()); } catch {}
      }
      return { success: true, data };
    } else {
      if (dispatch) {
        try { dispatch(refreshFailed(data?.error || `refresh ${response.status}`)); } catch {}
      }
      return { success: false, data };
    }
  } catch (error) {
    logError(dispatch, error);
    if (dispatch) {
      try {
        dispatch(refreshFailed(error instanceof Error ? error.message : 'refresh failed'));
      } catch {}
    }
    return { success: false, data: { error: 'Refresh request failed' } };
  }
}

/**
 * Refresh the session via Atoll's /auth/refresh endpoint.
 *
 * Single-flight: concurrent callers share one in-flight request. Called
 * both from the 401-retry path in atollFetch and from ensureFreshToken's
 * preemptive refresh. Refresh tokens are single-use, so parallel refresh
 * calls would race and invalidate each other.
 *
 * @param dispatch - Redux dispatch (for error surfacing)
 * @returns Object with success flag and the response data
 */
export function refreshSession(
  dispatch: any
): Promise<{ success: boolean; data: any }> {
  if (refreshingPromise) {
    // Concurrent caller — surface the wait so the dev header can show queue depth.
    if (dispatch) {
      try { dispatch(refreshQueueIncrement()); } catch {}
      return refreshingPromise.finally(() => {
        try { dispatch(refreshQueueDecrement()); } catch {}
      });
    }
    return refreshingPromise;
  }
  refreshingPromise = _performRefresh(dispatch).finally(() => {
    refreshingPromise = null;
  });
  return refreshingPromise;
}

/**
 * Preemptive token-freshness check. Call before every outgoing fetch.
 *
 * - Expired → awaits a single-flight refresh; all concurrent callers block.
 * - Near expiry (≤REFRESH_THRESHOLD_MS) → triggers a single-flight refresh
 *   in the background; the caller proceeds with the still-valid token.
 * - Fresh → returns immediately, no refresh.
 *
 * If expires_at is unknown (pre-upgrade session, or login predates this
 * logic), this is a no-op — the existing 401-retry path will handle it.
 */
export async function ensureFreshToken(dispatch: any): Promise<void> {
  const expiresAt = getTokenExpiresAt();
  if (!expiresAt) return;
  const timeLeft = expiresAt - Date.now();
  if (timeLeft <= 0) {
    await refreshSession(dispatch);
  } else if (timeLeft <= REFRESH_THRESHOLD_MS) {
    refreshSession(dispatch).catch(() => {});
  }
}

/**
 * Clear tokens and session state on logout.
 * Removes JWT and renewal token from sessionStorage, removes logged_in CSS class.
 * Every Atoll-compliant frontend calls this when the user logs out or auth fails.
 */
export function fnUserLoggedOut(): void {
  try {
    document.body.classList.remove("logged_in");
    sessionStorage.removeItem('jwt');
    localStorage.removeItem('jwt');
    clearRefreshToken();
    sessionStorage.removeItem('autheventresponse');
  } catch (e) {
    console.error(e);
  }
}
